import { useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { suggestNextStep } from "@/lib/nextStepPrompt";
import { useActionQueue } from "@/hooks/useActionQueue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, Clock, ArrowRight, Package,
  FileText, DollarSign, Zap, CalendarDays, Shield, Loader2, UserCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { OrderingModal } from "@/components/OrderingModal";
import { PermitQuickViewModal } from "@/components/quick-actions/PermitQuickViewModal";
import { RescheduleModal } from "@/components/quick-actions/RescheduleModal";
import { QuickAssignModal } from "@/components/quick-actions/QuickAssignModal";
import { QuickResolveIssueModal } from "@/components/quick-actions/QuickResolveIssueModal";
import { IntakeReviewWorkspace } from "@/components/IntakeReviewWorkspace";
import { useBuildReviewActions } from "@/hooks/useBuildReviewActions";
import type { Issue } from "@/data/types";
import {
  generateInvoicePlan,
  generateDepositInvoice,
  createAuditEntry,
} from "@/data/invoiceEngine";
import type { Job } from "@/data/types";
import { toast } from "sonner";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { getHandoffsForViewer } from "@/data/handoffRouting";
import { mergeAttentionFeed, syncOnQueueItemCompleted, type MergedAttentionEntry } from "@/data/workflowConvergence";
import { useActionQueueStore } from "@/data/actionQueue/context";
import { useUISkin } from "@/data/UISkinContext";
import { JobService } from "@/services/job.service";

/* ── Types ── */

type ActionMode = "modal" | "direct" | "navigate";

interface AttentionEntry {
  id: string;
  title: string;
  context: string;
  severity: "critical" | "warning" | "info";
  link: string;
  source: string;
  icon: React.ElementType;
  revenueAtRisk?: number;
  cta: string;
  /** How the CTA executes: opens a modal, runs immediately, or navigates */
  actionMode: ActionMode;
  /** Job linked to the action (used by modal/direct execution) */
  jobId?: string;
  /** Discriminator for direct/modal handlers */
  actionKind?: "generate_po" | "send_invoice" | "view_permit" | "resolve_issue" | "build_review";
  /** Phase 3: when set, CTA delegates to this handler (used by hand-off entries
   *  whose execution is already resolved via workflowConvergence). */
  onActivate?: () => void;
  /** Phase 3: optional secondary deep-link to the source operational object. */
  secondaryHref?: string;
  /** Phase 5: when this entry is a queue item chained from a hand-off. */
  linkedFromHandoffId?: string;
}

/* ── Priority & Styling ── */

const SEVERITY_WEIGHT: Record<string, number> = { critical: 100, warning: 50, info: 10 };

const SEVERITY_STYLE: Record<string, string> = {
  critical: "border-destructive/20 bg-destructive/5",
  warning: "border-warning/30 bg-warning/5",
  info: "border-border bg-muted/30",
};

const SEVERITY_DOT: Record<string, string> = {
  critical: "bg-destructive",
  warning: "bg-warning",
  info: "bg-muted-foreground",
};

/* ── Helpers ── */

const fmtK = (n: number) => (n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n.toFixed(0)}`);

function scorePriority(entry: AttentionEntry): number {
  // Severity sets a tier floor; revenue at risk orders within & across tiers.
  // Bumping rev weight so a $28K warning outranks a $5.8K critical when the
  // operator's real exposure demands it.
  const sevWeight = (SEVERITY_WEIGHT[entry.severity] ?? 10) * 2;
  const revWeight = Math.min((entry.revenueAtRisk || 0) / 500, 400);
  return sevWeight + revWeight;
}

/* ── Component ── */

export function WhatNeedsAttentionStrip() {
  const { salesOpportunities, appointmentResults, followUpTasks, setFollowUpTasks, jobs, setJobs, issues, buildReviewEvents } = useAppData();
  const buildReview = useBuildReviewActions();
  const navigate = useNavigate();
  const { role, roleInfo } = useRole();
  const { skin } = useUISkin();
  const actionQueue = useActionQueue();
  const persistedQueue = useActionQueueStore();
  const [orderingJob, setOrderingJob] = useState<Job | null>(null);
  const [permitJob, setPermitJob] = useState<Job | null>(null);
  const [rescheduleJob, setRescheduleJob] = useState<Job | null>(null);
  const [assignJob, setAssignJob] = useState<Job | null>(null);
  const [resolveIssue, setResolveIssue] = useState<Issue | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * Push-Mode chain memory. When the user picks a next-step from Ordering's
   * success panel we stash the success snapshot here so the follow-up modal
   * can offer a "Back" affordance that re-opens Ordering in success state.
   * Cleared whenever Ordering is opened fresh.
   */
  const [orderingChain, setOrderingChain] = useState<{
    job: Job;
    snapshot: { vendor: string; orderType: "Batch" | "Direct" | "Rush"; components: number };
  } | null>(null);

  /** Re-open OrderingModal in its success/picker state for a chained Back navigation. */
  const goBackToOrdering = useCallback(() => {
    if (!orderingChain) return;
    setPermitJob(null);
    setRescheduleJob(null);
    setAssignJob(null);
    setResolveIssue(null);
    setOrderingJob(orderingChain.job);
  }, [orderingChain]);


  /** Build the full runner set so suggestNextStep never falls back to navigate(). */
  const buildRunners = useCallback(
    (currentIssues: Issue[]) => ({
      openOrdering: (j: Job) => setOrderingJob(j),
      openReschedule: (j: Job) => setRescheduleJob(j),
      openQuickAssign: (j: Job) => setAssignJob(j),
      openResolveIssue: (j: Job) => {
        const top = currentIssues.find((i) => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
        if (top) setResolveIssue(top);
        else toast.info("No open issue to resolve");
      },
    }),
    [],
  );

  const entries = useMemo<AttentionEntry[]>(() => {
    const items: AttentionEntry[] = [];
    const seen = new Set<string>();

    const addIfNew = (entry: AttentionEntry) => {
      if (!seen.has(entry.id)) {
        seen.add(entry.id);
        items.push(entry);
      }
    };

    const now = new Date();

    // ── Missing appointment results (NAVIGATE — bulk multi-rep workflow) ──
    const missingResults = salesOpportunities.filter(
      (o) => o.stage === "Appointment Completed" && !appointmentResults.some((r) => r.opportunityId === o.id),
    );
    if (missingResults.length > 0) {
      const pipelineValue = missingResults.reduce(
        (s, o) => s + (o.totalEstimate || 0), 0,
      );
      addIfNew({
        id: "live-missing-results",
        title: `${missingResults.length} appointment${missingResults.length > 1 ? "s" : ""} need results recorded`,
        context: pipelineValue > 0
          ? `${fmtK(pipelineValue)} pipeline stalled — commissions and progression blocked`
          : "Pipeline progression and commission processing blocked until recorded",
        severity: "critical",
        link: "/opportunities",
        source: "Pipeline",
        icon: CalendarDays,
        revenueAtRisk: pipelineValue,
        cta: "Record Results",
        actionMode: "navigate",
      });
    }

    // ── Overdue follow-ups (NAVIGATE — requires per-task triage) ──
    const overdueFollowUps = followUpTasks.filter(
      (t) => t.status !== "completed" && new Date(t.dueDate) < now,
    );
    if (overdueFollowUps.length > 0) {
      addIfNew({
        id: "live-overdue-followups",
        title: `${overdueFollowUps.length} overdue follow-up${overdueFollowUps.length > 1 ? "s" : ""} — deals cooling`,
        context: "Immediate outreach needed before leads disengage",
        severity: overdueFollowUps.length >= 3 ? "critical" : "warning",
        link: "/follow-ups",
        source: "Follow-Up",
        icon: Clock,
        cta: "Open Follow-Up Queue",
        actionMode: "navigate",
      });
    }

    // ── Pending reschedules (NAVIGATE — requires calendar context) ──
    const reschedulesNeeded = appointmentResults.filter(
      (r) => r.resultType === "rescheduled" && !r.rescheduleDatetime,
    );
    if (reschedulesNeeded.length > 0) {
      addIfNew({
        id: "live-reschedules",
        title: `${reschedulesNeeded.length} appointment${reschedulesNeeded.length > 1 ? "s" : ""} need new dates`,
        context: "Unscheduled reschedules delay pipeline movement",
        severity: "warning",
        link: "/appointment-results",
        source: "Scheduling",
        icon: CalendarDays,
        cta: "Schedule Now",
        actionMode: "navigate",
      });
    }

    // ── Phase 3: Hand-offs merged into the unified attention surface ──
    // Hand-offs come pre-resolved (CTA, onActivate, source link) from the
    // central convergence module so the strip stays surgical.
    const visibleHandoffs = getHandoffsForViewer({ role, label: roleInfo.label }, followUpTasks);
    const merged = mergeAttentionFeed(
      visibleHandoffs,
      actionQueue.items,
      {
        navigate,
        jobs,
        issues,
        runners: {
          openOrdering: (j) => setOrderingJob(j),
          openReschedule: (j) => setRescheduleJob(j),
          openQuickAssign: (j) => setAssignJob(j),
          openResolveIssue: (j) => {
            const top = issues.find((i) => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
            if (top) setResolveIssue(top);
            else toast.info("No open issue to resolve");
          },
        },
        maxHandoffs: 3,
      },
      // Phase 4: persisted Action Queue items as the third source. Only items
      // visible to the current viewer (via the resolver) are passed in.
      persistedQueue.items.filter(
        (i) =>
          (i.status === "open" || i.status === "in_progress") &&
          (!i.assignedToRoleKey || i.assignedToRoleKey === role) &&
          (!i.assignedToUserId || true), // user-id matching skipped — no userId in viewer
      ),
      // Phase 5: queue completion runs through the centralized convergence
      // sync rule, which resolves any linked hand-off in the same pass.
      (id) => {
        const item = persistedQueue.items.find((i) => i.id === id);
        persistedQueue.completeItem(id);
        if (item) {
          const { resolvedHandoffIds, nextTasks } = syncOnQueueItemCompleted(
            { ...item, status: "completed" },
            followUpTasks,
          );
          if (resolvedHandoffIds.length > 0) {
            setFollowUpTasks(nextTasks);
            toast.success(
              resolvedHandoffIds.length === 1
                ? "Linked hand-off resolved"
                : `${resolvedHandoffIds.length} linked hand-offs resolved`,
            );
          }
        }
      },
    );
    for (const h of merged.handoffEntries) {
      addIfNew({
        id: h.id,
        title: h.title,
        context: h.context,
        severity: h.severity,
        link: h.secondaryHref ?? "/follow-ups",
        source: h.sourceLabel,
        icon: UserCheck,
        revenueAtRisk: undefined,
        cta: h.cta,
        actionMode: "direct",
        jobId: h.jobId,
        onActivate: h.onActivate,
        secondaryHref: h.secondaryHref,
      });
    }
    // Phase 4: persisted Action Queue entries — same render branch.
    for (const q of merged.queueEntries) {
      addIfNew({
        id: q.id,
        title: q.title,
        context: q.context,
        severity: q.severity,
        link: q.secondaryHref ?? "/follow-ups",
        source: q.sourceLabel,
        icon: UserCheck,
        revenueAtRisk: undefined,
        cta: q.cta,
        actionMode: "direct",
        jobId: q.jobId,
        onActivate: q.onActivate,
        secondaryHref: q.secondaryHref,
        linkedFromHandoffId: q.linkedFromHandoffId,
      });
    }

    // ── Action Queue critical items (suppression-aware via Phase 3 merge) ──
    const topActions = actionQueue.items
      .filter((item) => item.severity === "critical")
      .filter((item) => !merged.suppressedAiIds.has(item.id))
      // Drop any AQ card whose underlying issue has already been resolved.
      // Without this, "Resolve Issue" cards lingered after the operator
      // resolved the issue from this very panel (the AQ seed has no native
      // tie to live issue state).
      .filter((item) => {
        if (!item.jobId) return true;
        const stillHasOpenIssue = issues.some(
          (i) =>
            i.jobId === item.jobId &&
            (i.status === "Open" || i.status === "In Progress"),
        );
        const looksLikeIssueAction =
          /resolve|source alternative|schedule fix|alignment|discontinued/i.test(
            `${item.title} ${(item as any).primaryAction ?? ""} ${item.reason ?? ""}`,
          );
        // Only enforce on issue-flavored cards; finance / permit cards have
        // their own clearance signals already handled in useActionQueue.
        if (looksLikeIssueAction && !stillHasOpenIssue) return false;
        return true;
      })
      .slice(0, 3);

    for (const action of topActions) {
      const amountMatch = action.reason?.match(/\$([0-9,]+)/);
      const revenue = amountMatch ? parseFloat(amountMatch[1].replace(/,/g, "")) : undefined;
      const titleLower = action.title.toLowerCase();
      const reasonLower = (action.reason || "").toLowerCase();
      const primary = ((action as any).primaryAction ?? "").toString().toLowerCase();
      const isMaterial = titleLower.includes("material");
      const isPermit = titleLower.includes("permit");
      const isPayment = titleLower.includes("invoice") || titleLower.includes("payment");
      // Sourcing / QA-style "Resolve Issue" actions: discontinued material,
      // glass alignment, fix scheduling — these belong in the resolve flow.
      const isSourcingOrIssue =
        primary.includes("source alternative") ||
        primary.includes("schedule fix") ||
        titleLower.includes("alignment") ||
        titleLower.includes("discontinued") ||
        reasonLower.includes("blocking install") ||
        reasonLower.includes("blocking qa");

      let cta = "Resolve";
      let actionMode: ActionMode = "navigate";
      let actionKind: AttentionEntry["actionKind"];

      // Pre-spend gate: if Project Review / Rescission / Financial isn't cleared
      // for this job, demote the CTA to "Complete Project Review" — never expose
      // Generate PO / View Permit as primary actions on a gated job.
      const gatedJob = action.jobId ? jobs.find((j) => j.id === action.jobId) : undefined;
      const gateBlocked = gatedJob ? !derivePreSpendGate(gatedJob).cleared : false;

      if (isSourcingOrIssue && action.jobId) {
        cta = primary.includes("source") ? "Source & Resolve" : "Resolve Issue";
        actionMode = "modal";
        actionKind = "resolve_issue";
      } else if (isMaterial) {
        if (gateBlocked) {
          cta = "Complete Project Review";
          actionMode = "modal";
          actionKind = "build_review";
        } else {
          cta = "Generate PO";
          actionMode = action.jobId ? "modal" : "navigate";
          actionKind = "generate_po";
        }
      } else if (isPayment) {
        cta = "Send Invoice";
        actionMode = action.jobId ? "direct" : "navigate";
        actionKind = "send_invoice";
      } else if (isPermit) {
        if (gateBlocked) {
          cta = "Complete Project Review";
          actionMode = "modal";
          actionKind = "build_review";
        } else {
          cta = "View Permit";
          actionMode = action.jobId ? "modal" : "navigate";
          actionKind = "view_permit";
        }
      }

      addIfNew({
        id: `aq-${action.id}`,
        title: action.title.length > 55 ? action.title.slice(0, 52) + "…" : action.title,
        context: action.whyItMatters || "Operational blocker requiring immediate resolution",
        severity: "critical",
        link: action.link || "/jobs",
        source: "Operations",
        icon: isSourcingOrIssue ? AlertTriangle : isMaterial ? Package : isPermit ? FileText : isPayment ? DollarSign : AlertTriangle,
        revenueAtRisk: revenue,
        cta,
        actionMode,
        jobId: action.jobId,
        actionKind,
      });
    }

    // ── Revenue at risk from flagged jobs (NAVIGATE — requires per-job triage) ──
    const atRiskJobs = jobs.filter(
      (j) => !["Completed", "Issue"].includes(j.currentStage) && j.issueFlag,
    );
    if (atRiskJobs.length >= 2) {
      const totalRisk = atRiskJobs.reduce((s, j) => s + (j.financials?.contractValue || 0), 0);
      addIfNew({
        id: "revenue-at-risk-jobs",
        title: `${atRiskJobs.length} jobs with unresolved issues`,
        context: totalRisk > 0
          ? `${fmtK(totalRisk)} revenue at risk — delayed projects need triage`
          : "Active issues blocking project progression",
        severity: "warning",
        link: "/jobs",
        source: "Job Health",
        icon: AlertTriangle,
        revenueAtRisk: totalRisk,
        cta: "Open Issue Board",
        actionMode: "navigate",
      });
    }

    // Score, sort, and limit to top 5 (target 3–4)
    return items
      .sort((a, b) => scorePriority(b) - scorePriority(a))
      .slice(0, 5);
  }, [salesOpportunities, appointmentResults, followUpTasks, setFollowUpTasks, actionQueue.items, persistedQueue, jobs, issues, role, roleInfo.label, navigate]);

  /* ── Action handlers ── */

  const handleGeneratePO = useCallback((entry: AttentionEntry) => {
    if (!entry.jobId) return;
    const job = jobs.find((j) => j.id === entry.jobId);
    if (!job) {
      toast.error("Job not found");
      return;
    }
    // Pre-spend gate: ordering = direct company spend. If gates aren't cleared,
    // open the Build Review workspace inline (push mode) instead of opening
    // the ordering modal — operator finishes review without leaving the page.
    const gate = derivePreSpendGate(job);
    if (!gate.cleared) {
      toast.warning("Project Review required first", { description: gate.shortReason });
      buildReview.openReview(job);
      return;
    }
    // Fresh ordering — clear any prior chain memory.
    setOrderingChain(null);
    setOrderingJob(job);
  }, [jobs, buildReview]);

  const handleOrderingSubmit = useCallback(async (data: { jobId: string; vendor: string; orderType: "Batch" | "Direct" | "Rush"; notes: string }) => {
    // Stash chain memory so a follow-up modal can offer "Back" → success picker.
    const chainJob = jobs.find((j) => j.id === data.jobId);
    if (chainJob) {
      setOrderingChain({
        job: chainJob,
        snapshot: { vendor: data.vendor, orderType: data.orderType, components: (chainJob.materialsList?.length ?? 0) },
      });
    }
    if (!chainJob) return;
    const patch: Partial<Job> = {
      materialReadiness: "Ordered" as Job["materialReadiness"],
      planningStatus: "Ordered",
      selectedVendor: data.vendor,
      activityHistory: [
        ...(chainJob.activityHistory ?? []),
        {
          id: `act-${Date.now()}`,
          date: new Date().toISOString(),
          user: "system",
          action: `PO Generated — ${data.vendor} (${data.orderType})${data.notes ? ` · ${data.notes}` : ""}`,
        },
      ],
    };
    const { data: persisted, error } = await JobService.update(chainJob.id, patch);
    if (error || !persisted) {
      console.error("Failed to persist ordering update:", error);
      toast.error("Failed to generate PO");
      return;
    }
    setJobs((prev) => prev.map((j) => (j.id === persisted.id ? persisted : j)));
    toast.success("PO generated", { description: `${data.vendor} — ${data.orderType}` });
    // NOTE: do NOT close the modal here — the OrderingModal renders an inline
    // success state with next-step buttons so the user stays in context.
  }, [jobs, setJobs]);

  const handleSendInvoice = useCallback((entry: AttentionEntry) => {
    if (!entry.jobId) return;
    const job = jobs.find((j) => j.id === entry.jobId);
    if (!job) {
      toast.error("Job not found");
      return;
    }

    setBusyId(entry.id);

    setJobs((prev) => prev.map((j) => {
      if (j.id !== entry.jobId) return j;

      const existingInvoices = j.financials?.invoices ?? [];
      const draft = existingInvoices.find((inv) => inv.status === "draft");

      // If draft exists: mark as sent
      if (draft) {
        const updated = existingInvoices.map((inv) =>
          inv.id === draft.id ? { ...inv, status: "sent" as const } : inv,
        );
        return {
          ...j,
          financials: { ...j.financials!, invoices: updated },
          activityHistory: [
            ...(j.activityHistory ?? []),
            {
              id: `act-${Date.now()}`,
              date: new Date().toISOString(),
              user: "system",
              action: `Invoice ${draft.id} sent to customer ($${draft.amount.toLocaleString()})`,
            },
          ],
        };
      }

      // Otherwise: generate plan + deposit invoice and send it
      const plan = generateInvoicePlan(j);
      const deposit = generateDepositInvoice(j);
      if (!deposit) {
        toast.info("No invoice could be generated for this job");
        return j;
      }
      const sent = { ...deposit, status: "sent" as const };
      return {
        ...j,
        financials: {
          ...j.financials!,
          invoicePlan: plan,
          invoices: [...existingInvoices, sent],
        },
        activityHistory: [
          ...(j.activityHistory ?? []),
          {
            id: `act-${Date.now() + 1}`,
            date: new Date().toISOString(),
            user: "system",
            action: `Invoice plan generated and deposit sent ($${sent.amount.toLocaleString()})`,
          },
        ],
      };
    }));

    // Audit (kept for future panel integration)
    createAuditEntry("invoice_sent", `Invoice sent from Attention Strip for ${entry.jobId}`);

    setTimeout(() => {
      setBusyId(null);
      toast.success("Invoice sent");
    }, 250);
  }, [jobs, setJobs]);

  const renderCTA = (entry: AttentionEntry) => {
    // Phase 3 — hand-off entries arrive with a fully-resolved onActivate
    // (resolveHandoffAction → runAction). Honor it directly so we don't
    // re-derive intent or fall through to the legacy actionKind ladder.
    if (entry.onActivate) {
      return (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[10px] px-2.5 gap-1 font-medium"
          onClick={entry.onActivate}
        >
          {entry.cta}
          <ArrowRight className="h-3 w-3" />
        </Button>
      );
    }

    const isBusy = busyId === entry.id;

    if (entry.actionMode === "modal" && entry.actionKind === "build_review") {
      return (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[10px] px-2.5 gap-1 font-medium"
          onClick={() => {
            const j = jobs.find((x) => x.id === entry.jobId);
            if (j) buildReview.openReview(j);
            else toast.error("Job not found");
          }}
        >
          {entry.cta}
          <Shield className="h-3 w-3" />
        </Button>
      );
    }

    if (entry.actionMode === "modal" && entry.actionKind === "generate_po") {
      return (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[10px] px-2.5 gap-1 font-medium"
          onClick={() => handleGeneratePO(entry)}
        >
          {entry.cta}
          <Package className="h-3 w-3" />
        </Button>
      );
    }

    if (entry.actionMode === "modal" && entry.actionKind === "view_permit") {
      return (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[10px] px-2.5 gap-1 font-medium"
          onClick={() => {
            const j = jobs.find((x) => x.id === entry.jobId);
            if (j) setPermitJob(j);
            else toast.error("Job not found");
          }}
        >
          {entry.cta}
          <FileText className="h-3 w-3" />
        </Button>
      );
    }

    if (entry.actionMode === "modal" && entry.actionKind === "resolve_issue") {
      return (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[10px] px-2.5 gap-1 font-medium"
          onClick={() => {
            if (!entry.jobId) return;
            const top = issues.find(
              (i) => i.jobId === entry.jobId && (i.status === "Open" || i.status === "In Progress"),
            );
            if (top) setResolveIssue(top);
            else toast.info("No open issue found for this job");
          }}
        >
          {entry.cta}
          <AlertTriangle className="h-3 w-3" />
        </Button>
      );
    }

    if (entry.actionMode === "direct" && entry.actionKind === "send_invoice") {
      return (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[10px] px-2.5 gap-1 font-medium"
          onClick={() => handleSendInvoice(entry)}
          disabled={isBusy}
        >
          {entry.cta}
          {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />}
        </Button>
      );
    }

    // Default: navigate (for genuinely complex flows)
    return (
      <Link to={entry.link} className="shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[10px] px-2.5 gap-1 font-medium"
        >
          {entry.cta}
          <ArrowRight className="h-3 w-3" />
        </Button>
      </Link>
    );
  };

  /* ── Cockpit-skin render: hairline list, no badges, no decorative icons,
        identical data + handlers + CTAs as the classic strip below. ── */
  const cockpitStrip = (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[17px] font-semibold tracking-[-0.014em] text-foreground">
          What Needs Attention
        </h2>
        <span className="text-[13px] text-muted-foreground tabular-nums">
          {entries.length} Item{entries.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="rounded-[10px] border border-border bg-card overflow-hidden">
        <ul className="divide-y divide-border">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/30 transition-colors"
            >
              {/* Status dot only — no decorative icon. */}
              <span
                className={cn("h-1.5 w-1.5 rounded-full shrink-0", SEVERITY_DOT[entry.severity])}
                aria-hidden
              />
              <div className="flex-1 min-w-0">
                <p className="text-[14px] text-foreground leading-snug truncate">
                  {entry.title}
                </p>
                <p className="text-[12px] text-muted-foreground leading-snug mt-0.5 truncate">
                  {entry.context}
                </p>
              </div>
              <span className="text-[11px] text-muted-foreground/70 hidden md:inline shrink-0 tabular-nums">
                {entry.source}
              </span>
              {entry.jobId && entry.actionMode !== "navigate" && (
                <Link
                  to={`/jobs/${entry.jobId}`}
                  className="text-[12px] text-muted-foreground hover:text-foreground hidden lg:inline shrink-0"
                >
                  Open job
                </Link>
              )}
              <div className="shrink-0">{renderCTA(entry)}</div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );

  /* ── Classic-skin render: original card-based strip. Untouched behavior. ── */
  const classicStrip = (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-primary" />
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
            What Needs Attention
          </p>
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 ml-1">
            {entries.length}
          </Badge>
        </div>
        <div className="flex items-center gap-1 text-[9px] text-muted-foreground/50">
          <Shield className="h-2.5 w-2.5" />
          <span>Nothing runs without your approval</span>
        </div>
      </div>

      {/* Items */}
      <div className="grid gap-2.5">
        {entries.map((entry) => {
          const Icon = entry.icon;
          return (
            <div
              key={entry.id}
              className={cn(
                "flex items-center gap-3 rounded-lg border px-3.5 py-3 transition-colors group",
                SEVERITY_STYLE[entry.severity],
              )}
            >
              {/* Icon */}
              <div className="shrink-0">
                <Icon className={cn(
                  "h-4 w-4",
                  entry.severity === "critical" ? "text-destructive" :
                  entry.severity === "warning" ? "text-warning" :
                  "text-muted-foreground",
                )} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-foreground truncate">{entry.title}</span>
                  <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", SEVERITY_DOT[entry.severity])} />
                  {entry.linkedFromHandoffId && (
                    <Badge
                      variant="outline"
                      className="text-[9px] px-1.5 py-0 font-normal text-muted-foreground border-muted-foreground/30"
                      title={`Chained from hand-off ${entry.linkedFromHandoffId}`}
                    >
                      ← from hand-off
                    </Badge>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground leading-snug mt-0.5 line-clamp-1">
                  {entry.context}
                </p>
              </div>

              {/* Source label */}
              <span className="text-[9px] text-muted-foreground/50 hidden md:inline shrink-0">
                {entry.source}
              </span>

              {/* Secondary "Open Job" link when action is direct/modal */}
              {entry.jobId && entry.actionMode !== "navigate" && (
                <Link
                  to={`/jobs/${entry.jobId}`}
                  className="text-[10px] text-muted-foreground hover:text-foreground hidden lg:inline shrink-0"
                  title="Open job for full context"
                >
                  Open job
                </Link>
              )}

              {/* Primary CTA */}
              <div className="shrink-0">
                {renderCTA(entry)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (entries.length === 0) return null;

  return (
    <div className="space-y-3">
      {skin === "cockpit" ? cockpitStrip : classicStrip}

      {/* PO Modal — opens directly from "Generate PO" CTA. Stays open after
          submit and shows an inline next-step picker so user never bounces. */}
      {orderingJob && (
        <OrderingModal
          open={!!orderingJob}
          onOpenChange={(open) => { if (!open) setOrderingJob(null); }}
          job={orderingJob}
          openIssues={issues.filter(i => i.jobId === orderingJob.id && (i.status === "Open" || i.status === "In Progress"))}
          onSendToOrdering={handleOrderingSubmit}
          reopenSuccess={orderingChain && orderingChain.job.id === orderingJob.id ? orderingChain.snapshot : null}
          onChooseNextStep={(step) => {
            const justOrdered = jobs.find(j => j.id === orderingJob.id) ?? orderingJob;
            const projected: Job = { ...justOrdered, materialReadiness: "Ordered" as Job["materialReadiness"] };
            setOrderingJob(null);
            if (step.kind === "done") { setOrderingChain(null); return; }
            if (step.kind === "assign_crew")   { setAssignJob(projected); return; }
            if (step.kind === "reschedule")    { setRescheduleJob(projected); return; }
            if (step.kind === "view_permit")   { setPermitJob(projected); return; }
            if (step.kind === "resolve_issue") {
              const top = issues.find(i => i.jobId === projected.id && (i.status === "Open" || i.status === "In Progress"));
              if (top) setResolveIssue(top);
              else toast.info("No open issue to resolve");
              return;
            }
            if (step.kind === "navigate") { setOrderingChain(null); navigate(step.to); }
          }}
        />
      )}

      <PermitQuickViewModal
        open={!!permitJob}
        onOpenChange={(open) => { if (!open) setPermitJob(null); }}
        job={permitJob}
        onBack={orderingChain && permitJob && orderingChain.job.id === permitJob.id ? goBackToOrdering : undefined}
        onSuccess={(updatedJob) => {
          setOrderingChain(null);
          const oi = issues.filter(i => i.jobId === updatedJob.id && (i.status === "Open" || i.status === "In Progress"));
          suggestNextStep({ job: updatedJob, openIssues: oi, navigate, successLabel: "Permit updated — what's next?", runners: buildRunners(issues) });
        }}
        onChooseNextStep={(step, updatedJob) => {
          setPermitJob(null);
          setOrderingChain(null);
          if (step.kind === "done") return;
          if (step.kind === "assign_crew")   { setAssignJob(updatedJob); return; }
          if (step.kind === "reschedule")    { setRescheduleJob(updatedJob); return; }
          if (step.kind === "view_permit")   { setPermitJob(updatedJob); return; }
          if (step.kind === "generate_po")   { setOrderingJob(updatedJob); return; }
          if (step.kind === "resolve_issue") {
            const top = issues.find(i => i.jobId === updatedJob.id && (i.status === "Open" || i.status === "In Progress"));
            if (top) setResolveIssue(top); else toast.info("No open issue to resolve");
            return;
          }
          if (step.kind === "navigate") navigate(step.to);
        }}
      />

      <RescheduleModal
        open={!!rescheduleJob}
        onOpenChange={(open) => { if (!open) setRescheduleJob(null); }}
        job={rescheduleJob}
        openIssues={rescheduleJob ? issues.filter(i => i.jobId === rescheduleJob.id && (i.status === "Open" || i.status === "In Progress")) : []}
        onBack={orderingChain && rescheduleJob && orderingChain.job.id === rescheduleJob.id ? goBackToOrdering : undefined}
        onChooseNextStep={(step, updatedJob) => {
          setRescheduleJob(null);
          setOrderingChain(null);
          if (step.kind === "done") return;
          if (step.kind === "assign_crew")   { setAssignJob(updatedJob); return; }
          if (step.kind === "reschedule")    { setRescheduleJob(updatedJob); return; }
          if (step.kind === "view_permit")   { setPermitJob(updatedJob); return; }
          if (step.kind === "generate_po")   { setOrderingJob(updatedJob); return; }
          if (step.kind === "resolve_issue") {
            const top = issues.find(i => i.jobId === updatedJob.id && (i.status === "Open" || i.status === "In Progress"));
            if (top) setResolveIssue(top); else toast.info("No open issue to resolve");
            return;
          }
          if (step.kind === "navigate") navigate(step.to);
        }}
      />

      <QuickAssignModal
        open={!!assignJob}
        onOpenChange={(open) => { if (!open) setAssignJob(null); }}
        job={assignJob}
        openIssues={assignJob ? issues.filter(i => i.jobId === assignJob.id && (i.status === "Open" || i.status === "In Progress")) : []}
        onBack={orderingChain && assignJob && orderingChain.job.id === assignJob.id ? goBackToOrdering : undefined}
        onChooseNextStep={(step, updatedJob) => {
          setAssignJob(null);
          setOrderingChain(null);
          if (step.kind === "done") return;
          if (step.kind === "assign_crew")   { setAssignJob(updatedJob); return; }
          if (step.kind === "reschedule")    { setRescheduleJob(updatedJob); return; }
          if (step.kind === "view_permit")   { setPermitJob(updatedJob); return; }
          if (step.kind === "generate_po")   { setOrderingJob(updatedJob); return; }
          if (step.kind === "resolve_issue") {
            const top = issues.find(i => i.jobId === updatedJob.id && (i.status === "Open" || i.status === "In Progress"));
            if (top) setResolveIssue(top); else toast.info("No open issue to resolve");
            return;
          }
          if (step.kind === "navigate") navigate(step.to);
        }}
      />

      <QuickResolveIssueModal
        open={!!resolveIssue}
        onOpenChange={(open) => { if (!open) setResolveIssue(null); }}
        issue={resolveIssue}
        onBack={orderingChain && resolveIssue && orderingChain.job.id === resolveIssue.jobId ? goBackToOrdering : undefined}
        onChooseNextStep={(step, job) => {
          const justResolvedId = resolveIssue?.id;
          setResolveIssue(null);
          setOrderingChain(null);
          if (step.kind === "done") return;
          if (step.kind === "assign_crew")   { setAssignJob(job); return; }
          if (step.kind === "reschedule")    { setRescheduleJob(job); return; }
          if (step.kind === "view_permit")   { setPermitJob(job); return; }
          if (step.kind === "generate_po")   { setOrderingJob(job); return; }
          if (step.kind === "resolve_issue") {
            const top = issues.find(i => i.jobId === job.id && i.id !== justResolvedId && (i.status === "Open" || i.status === "In Progress"));
            if (top) setResolveIssue(top); else toast.info("No more open issues");
            return;
          }
          if (step.kind === "navigate") navigate(step.to);
        }}
      />

      {/* Build Review workspace — opens inline in push mode when a gated job's
          "Complete Project Review" CTA is clicked. Operator approves/revises
          without leaving the dashboard, then can chain into Order/Permit. */}
      <IntakeReviewWorkspace
        job={buildReview.reviewJob}
        open={!!buildReview.reviewJob}
        lane="needs-review"
        buildReviewEvents={buildReviewEvents}
        openIssues={issues}
        onClose={buildReview.closeReview}
        onApprove={buildReview.onApprove}
        onApproveAndContinue={buildReview.commitApproval}
        onContinueChain={(approvedJob, step) => {
          buildReview.closeReview();
          if (step.kind === "done") return;
          if (step.kind === "generate_po")   { setOrderingJob(approvedJob); return; }
          if (step.kind === "view_permit")   { setPermitJob(approvedJob); return; }
          if (step.kind === "assign_crew")   { setAssignJob(approvedJob); return; }
          if (step.kind === "reschedule")    { setRescheduleJob(approvedJob); return; }
          if (step.kind === "resolve_issue") {
            const top = issues.find(i => i.jobId === approvedJob.id && (i.status === "Open" || i.status === "In Progress"));
            if (top) setResolveIssue(top); else toast.info("No open issue to resolve");
            return;
          }
          if (step.kind === "navigate") navigate(step.to);
        }}
        onRequestRevision={buildReview.onRequestRevision}
        onSendToSales={buildReview.onSendToSales}
        onSalesApprove={buildReview.onSalesApprove}
        onCustomerSigned={buildReview.onCustomerSigned}
        onFinalApproval={buildReview.onFinalApproval}
      />
    </div>
  );
}
